import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Icon, Tooltip } from "@rentiohq/web-shared/dist/components";
import React from "react";
import {
  isDocumentCountIncomplete,
  shouldShowDocumentCount,
  shouldShowHousehold,
} from "utils/application.utils";
import { BARE_TOOLTIP_TRIGGER_STYLE } from "../../tooltipTrigger.styles";
import { getApplicationIndicators } from "./applicationIndicators.utils";
import * as S from "./ApplicationTable.styled";

/**
 * The "More info" indicators, shared by the candidate list and the other-applications
 * dialog (DEV-3122).
 *
 * They live here rather than in the row because two surfaces now show the same
 * facts about the same record, and this week produced three tickets that all
 * came down to two surfaces disagreeing. Copying the markup would have made a
 * fourth; rendering the same component cannot drift.
 */

// Every indicator in the "More info" column is the same shape: an icon plus a
// number, with a tooltip naming what the number counts. Nothing in that column
// is self-explanatory from its icon alone, so they all carry one. The number is
// gray unless it is what cost this candidate a perfect match, in which case it
// is red (DEV-3075).
//
// The tooltip is the shared Tooltip component, not the native title attribute:
// the browser holds a title back for roughly a second, which reads as broken.
// This one is built on floating-ui's useHover with no delay, so it opens on
// entry, exactly like the score dash and the warning triangle in this table.
export const InfoIndicator = (props: {
  icon: React.ReactNode;
  value: React.ReactNode;
  tooltip: string;
  /** Red when this number is what kept the match score below 100 (DEV-3075). */
  isBelowPerfect?: boolean;
}) => {
  const { icon, value, tooltip, isBelowPerfect } = props;

  return (
    <Tooltip
      // Inherit the column's color on top of the shared reset, so a
      // currentColor icon does not turn black.
      style={{ ...BARE_TOOLTIP_TRIGGER_STYLE, color: "inherit" }}
      tooltipContent={tooltip}
      placement="bottom"
    >
      <S.InfoItem>
        {icon}
        <S.InfoValue isBelowPerfect={isBelowPerfect}>{value}</S.InfoValue>
      </S.InfoItem>
    </Tooltip>
  );
};

// The identity and income documents follow that same shape: no status dot, no
// color, just delivered out of required. The icon inherits its color from the
// column (currentColor) and is drawn at stroke width 1, so it carries the same
// weight as the shared Icon components next to it rather than standing out as a
// heavier glyph.
// Both numbers count DOCUMENTS. An indicator that requires none is left out
// instead of showing "0/0": a property in Brussels asks for no identity
// document, and a candidate who declared no income source owes no income proof,
// so "0/0" would read as "nothing delivered" where the truth is "nothing owed".
// A candidate's income itself is not this indicator's job: that is what the
// income column shows.
// A document the candidate announced they will bring in person is deliberately
// NOT set apart here: it counts as required and not delivered, like any document
// the broker does not have. The reasoning is at the counting itself, where
// idDocsRequired is derived in ApplicationTableRow.
//
// Red on the delivered number, and only on that number, as soon as one document
// is short (DEV-3075). The total is not the problem, the shortfall is. Same
// treatment the older non-Rentmatch list has always given it, down to the same
// red, so the two lists say the same thing in the same way.
export const DocumentCountItem = (props: {
  type: "id" | "income";
  delivered: number;
  /** From the row, not derived. Null on a submission older than the contract. */
  required?: number | null;
  title: string;
}) => {
  const { type, delivered, required, title } = props;

  // Nothing owed, or a row from before the counts existed: no fraction. That
  // one test is the whole gate now, and it is why a visit request shows nothing
  // on every tab without any screen knowing what a visit request is.
  if (!shouldShowDocumentCount(required)) {
    return null;
  }

  const isIncomplete = isDocumentCountIncomplete(delivered, required);

  return (
    <InfoIndicator
      tooltip={`${title}: ${delivered}/${required}`}
      value={
        <>
          <S.DeliveredCount isIncomplete={isIncomplete}>
            {delivered}
          </S.DeliveredCount>
          {`/${required}`}
        </>
      }
      icon={
        type === "id" ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <circle cx="8" cy="12" r="2.2" />
            <path d="M13 10.5h5M13 14h5" />
          </svg>
        ) : (
          <S.IncomeDocumentGlyph>€</S.IncomeDocumentGlyph>
        )
      }
    />
  );
};

/**
 * Everything the "More info" column says about one application: household,
 * pets, and the two document fractions. Given the application itself rather
 * than a dozen derived numbers, so a caller cannot compute one of them its own
 * way.
 *
 * `householdBelowPerfect` is optional because the red marking is score-based
 * and the score belongs to a property. The candidate list has one property in
 * view and passes it; the other-applications dialog spans several and does not,
 * so nothing there is red on account of the match (decided by Jonas on
 * 2026-09-06). The document fraction still reddens in both, because that one
 * is not score-based.
 */
export const ApplicationInfoIndicators = (props: {
  application: IApplication;
  householdBelowPerfect?: boolean;
}) => {
  const { application, householdBelowPerfect } = props;
  const { adultCount, childrenCount, petCount, idDocs, incomeDocs } =
    getApplicationIndicators(application);

  return (
    <S.InfoGroup>
      {shouldShowHousehold(adultCount) && (
        <>
          <InfoIndicator
            icon={<Icon source="contact" size="small" />}
            value={adultCount}
            isBelowPerfect={householdBelowPerfect}
            tooltip={`${getLocalizedText(
              "INTAKE_RESIDENT_ADULTS",
            )}: ${adultCount}`}
          />
          <InfoIndicator
            icon={<Icon source="propellerHat" size="small" />}
            value={childrenCount}
            isBelowPerfect={householdBelowPerfect}
            tooltip={`${getLocalizedText(
              "INTAKE_RESIDENT_CHILDREN",
            )}: ${childrenCount}`}
          />
        </>
      )}
      {petCount > 0 && (
        <InfoIndicator
          icon={<Icon source="pets" size="small" />}
          value={petCount}
          tooltip={`${getLocalizedText("editor.variable.pets")}: ${petCount}`}
        />
      )}
      <DocumentCountItem
        type="id"
        delivered={idDocs.delivered}
        required={idDocs.required}
        title={getLocalizedText("intake.contract.id.title")}
      />
      <DocumentCountItem
        type="income"
        delivered={incomeDocs.delivered}
        required={incomeDocs.required}
        title={getLocalizedText("intake.financial.title")}
      />
    </S.InfoGroup>
  );
};
